import chatService from '@/services/chatService';

function truncateTitle(str) {
  if (!str) return '';
  return str.length > 30 ? `${str.substring(0, 30)  }…` : str;
}

const state = {
  conversations: [],
  activeConversation: null,
  messages: [],
  loading: false,
  sidebarLoading: false,
  streaming: false,
  error: null,
  pagination: { page: 1, limit: 20, hasMore: false }
};

const mutations = {
  setConversations(state, conversations) {
    state.conversations = conversations;
  },
  appendConversations(state, conversations) {
    state.conversations = [...state.conversations, ...conversations];
  },
  setActiveConversation(state, conversation) {
    state.activeConversation = conversation;
  },
  setMessages(state, messages) {
    state.messages = messages;
  },
  addMessage(state, message) {
    state.messages = [...state.messages, message];
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setSidebarLoading(state, loading) {
    state.sidebarLoading = loading;
  },
  setStreaming(state, streaming) {
    state.streaming = streaming;
  },
  setError(state, error) {
    state.error = error;
  },
  removeConversation(state, id) {
    state.conversations = state.conversations.filter(c => c.id !== id);
  },
  clearActive(state) {
    state.activeConversation = null;
    state.messages = [];
    state.error = null;
  },
  setPagination(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination };
  }
};

const actions = {
  async fetchConversations({ dispatch, commit, state }, { page, limit } = {}) {
    commit('setSidebarLoading', true);

    const queryPage = page || state.pagination.page;
    const queryLimit = limit || state.pagination.limit;

    try {
      const response = await chatService.listConversations({ page: queryPage, limit: queryLimit });
      const conversations = (response.data || response || []).map(c => ({
        ...c,
        title: truncateTitle(c.title || '')
      }));

      if (queryPage === 1) {
        commit('setConversations', conversations);
      } else {
        commit('appendConversations', conversations);
      }

      const hasMore = conversations.length >= queryLimit;
      commit('setPagination', { page: queryPage, limit: queryLimit, hasMore });
    } catch (e) {
      commit('setError', e.message || 'Failed to fetch conversations');
      dispatch('common/handleServiceException', { e }, { root: true });
    } finally {
      commit('setSidebarLoading', false);
    }
  },

  async fetchConversation({ dispatch, commit }, id) {
    commit('setLoading', true);
    commit('setError', null);

    try {
      const response = await chatService.getConversation(id);
      const data = response.data || response;
      const conversation = data.conversation || data;
      const messages = data.messages || conversation.messages || [];
      commit('setActiveConversation', conversation);
      commit('setMessages', messages);
    } catch (e) {
      commit('setError', e.message || 'Failed to fetch conversation');
      dispatch('common/handleServiceException', { e }, { root: true });
    } finally {
      commit('setLoading', false);
    }
  },

  async sendMessage({ dispatch, commit, state }, { message }) {
    commit('setError', null);

    const userMessage = {
      id: `msg-${  Date.now()}`,
      role: 'user',
      content_type: 'text',
      content: message,
      timestamp: new Date().toISOString()
    };
    commit('addMessage', userMessage);
    commit('setStreaming', true);

    try {
      const conversationId = state.activeConversation ? state.activeConversation.id : null;
      const response = await chatService.sendMessage({ conversationId, message });
      const data = response.data || response;

      const convId = data.conversationId || data.conversation_id;
      if (convId) {
        commit('setActiveConversation', { id: convId });
        dispatch('fetchConversations', { page: 1 });
      }

      const assistantMessage = {
        id: `msg-${  Date.now()  }-assistant`,
        role: 'assistant',
        content_type: 'text',
        content: data.content || data.message || '',
        timestamp: new Date().toISOString()
      };
      commit('addMessage', assistantMessage);
    } catch (e) {
      commit('setError', e.message || 'Failed to send message');
      dispatch('common/handleServiceException', { e }, { root: true });
    } finally {
      commit('setStreaming', false);
    }
  },

  async deleteConversation({ dispatch, commit, state }, id) {
    commit('setError', null);

    try {
      await chatService.deleteConversation(id);
      commit('removeConversation', id);

      if (state.activeConversation && state.activeConversation.id === id) {
        commit('clearActive');
      }
    } catch (e) {
      commit('setError', e.message || 'Failed to delete conversation');
      dispatch('common/handleServiceException', { e }, { root: true });
    }
  },

  clearActiveConversation({ commit }) {
    commit('clearActive');
  }
};

const getters = {};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};

export { truncateTitle };
